import { createAction } from "@reduxjs/toolkit";
import { INVALIDATE_MEDIA_UPLOAD, UPLOAD_MEDIA_REQUEST, UPLOADING_MEDIA_FAILED, UPLOADING_MEDIA_SUCCEEDED } from "./actionTypes";
import buildUrl from "build-url-ts";
import axios from "axios";
import { takeEvery, call, put } from "redux-saga/effects";
import { MediaFile, UploadMediaErrorResponse, UploadMediaRequest, UploadMediaResponse } from "../../types/media";
import S3Upload from 'react-s3-uploader/s3upload';
import { getAccessToken } from "../../utils/authentication.service";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_API_URL}`;

export const uploadMedia = createAction<UploadMediaRequest>(UPLOAD_MEDIA_REQUEST);
export const uploadingMediaSucceeded = createAction<UploadMediaResponse>(UPLOADING_MEDIA_SUCCEEDED);
export const uploadingMediaFailed = createAction<UploadMediaErrorResponse>(UPLOADING_MEDIA_FAILED);

const uploadMediaSaga = function* (action: ReturnType<typeof uploadMedia>): any {
  const { media } = action.payload as UploadMediaRequest;

  const file = media.file;
  if (!file) {
    return;
  }

  const fileResource = yield call(uploadS3, file);
  const url = buildUrl(apiUrl, {
    path: "/api/2.0/media/upload",
  });

  const request = {
    signedUrl: fileResource.signResult.signedUrl,
    width: media.width || 0,
    height: media.height || 0,
    contentType: media.contentType || file.type,
    mediaType: media.type,
  }

  const createMediaApi = () => {
    return axios({
      method: 'POST',
      url: url,
      data: request
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createMediaApi);
    yield put(uploadingMediaSucceeded({ id: media.id, response: response }));
  } catch (error) {
    yield put(uploadingMediaFailed({ id: media.id, error: error }));
  }
};

const uploadS3 = function (media: File): any {
  return new Promise((resolve, reject) => {
    const fileName = media.name || 'media';

    const s3Uploader = new S3Upload({
      signingUrl: "/api/2.0/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
      server: apiUrl,
      scrubFilename: (filename) => {
        const name = filename.replace(/[^\w\d_\-.\s]+/ig, "");
        return encodeURIComponent(name);
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(fileName) },
      contentDisposition: "auto",
      uploadRequestHeaders: {},
      onFinishS3Put: (signResult, file) => {
        resolve({
          signResult,
          file,
        });
      },
      onError: () => {
        reject();
      },
      files: [media],
    });
  });
}

export const invalidateMediaUpload = createAction<string>(INVALIDATE_MEDIA_UPLOAD);

export default function* mediaSaga() {
  yield takeEvery(UPLOAD_MEDIA_REQUEST, uploadMediaSaga);
};